import {useState, useRef, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Icon, Button} from '@/ui-kits';
import {transcribeAudioRequest, resetSpecialistDashboardMessage} from '@/services/redux/slices/specialistDashboardSlice';
import {transcribeAudioLoadingStatus, transcribedTextStatus, specialistDashboardSuccessStatus, specialistDashboardFailureStatus} from '@/services/redux/selectors/specialistDashboardSelector';
import {useToast} from '@/hooks';

const BAR_COUNT = 250;

function WaveformBars() {
  const [bars, setBars] = useState<number[]>(new Array(BAR_COUNT).fill(0.3));

  useEffect(() => {
    const interval = setInterval(() => {
      setBars(prev => prev.map(() => 0.2 + Math.random() * 0.8));
    }, 150);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-[2px] h-10 flex-1 min-w-0 overflow-hidden">
      {bars.map((h, i) => (
        <div
          key={i}
          className="w-[2px] bg-ui_input_border_dark rounded-full transition-all duration-150 flex-shrink-0"
          style={{height: `${h * 100}%`}}
        />
      ))}
    </div>
  );
}

interface SpeechToTextRecorderProps {
  onTranscriptionComplete: (text: string) => void;
  onRecordingStateChange?: (isRecording: boolean) => void;
  disabled?: boolean;
}

export const SpeechToTextRecorder = ({onTranscriptionComplete, onRecordingStateChange, disabled}: SpeechToTextRecorderProps) => {
  const dispatch = useDispatch();
  const {showToast} = useToast();

  const [isRecording, setIsRecording] = useState(false);

  useEffect(() => {
    onRecordingStateChange?.(isRecording);
  }, [isRecording, onRecordingStateChange]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const transcribeLoading = useSelector(transcribeAudioLoadingStatus);
  const transcribeSuccess = useSelector(specialistDashboardSuccessStatus);
  const transcribeFailure = useSelector(specialistDashboardFailureStatus);
  const transcribedText = useSelector(transcribedTextStatus);

  // Handle transcription result
  useEffect(() => {
    if (transcribeSuccess === 'S-10123' && transcribedText) {
      onTranscriptionComplete(transcribedText);
      dispatch(resetSpecialistDashboardMessage());
    }
    if (transcribeFailure) {
      showToast('Speech-to-text failed. Please try again.', 'failure');
      dispatch(resetSpecialistDashboardMessage());
    }
  }, [transcribeSuccess, transcribeFailure, transcribedText, dispatch, onTranscriptionComplete, showToast]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, {mimeType});
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start();
      setIsRecording(true);
    } catch {
      showToast('Microphone access denied.', 'failure');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    streamRef.current?.getTracks().forEach(t => t.stop());
    setIsRecording(false);
  };

  const handleConfirm = () => {
    stopRecording();
    setTimeout(() => {
      if (!chunksRef.current.length) return;
      const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, {type: mimeType});
      const formData = new FormData();
      formData.append('file', blob, `recording.${mimeType.includes('webm') ? 'webm' : 'mp4'}`);
      dispatch(transcribeAudioRequest(formData));
    }, 100);
  };

  const handleCancel = () => {
    stopRecording();
    chunksRef.current = [];
  };

  // Recording state - show waveform with cancel/confirm buttons
  if (isRecording) {
    return (
      <div className="z-50 relative bg-white rounded-lg flex items-center gap-2 sm:gap-3 w-full px-2 sm:px-3 py-2 sm:py-3">
        <WaveformBars />
        <button onClick={handleCancel} className="flex-shrink-0 p-1 sm:p-1.5 hover:bg-red-50 rounded-full transition-colors" title="Cancel">
          <Icon type="close_mic" className="size-6 sm:size-7" />
        </button>
        <button onClick={handleConfirm} className="flex-shrink-0 p-1 sm:p-1.5 hover:bg-green-50 rounded-full transition-colors" title="Confirm">
          <Icon type="check" className="size-6 sm:size-7" />
        </button>
      </div>
    );
  }

  // Transcribing state
  if (transcribeLoading) {
    return (
      <div className="flex items-center gap-2">
        <div className="size-5 border-2 border-ui_blue border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-ui_grey">Transcribing...</span>
      </div>
    );
  }

  // Idle state - show mic button
  return (
    <button
      onClick={startRecording}
      disabled={disabled}
      className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 transition-colors"
      title="Start voice input"
    >
      <Icon type="mic" className="size-6 sm:size-7" />
    </button>
  );
};
